import {
  formatImagingModalityDisplay,
  formatPriorityDisplay,
  priorityUsesUrgentPill,
} from '../worklist/imagingWorklistTableUtils';
import type { PeerReview, PeerReviewStatus } from '../../services/peerReviewService';
import { getPeerReviewStudyFields } from '../../services/peerReviewService';

const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

function parseClock(time: string): string | null {
  const trimmed = time.trim();
  if (!trimmed) {
    return null;
  }
  if (/^\d{6}$/.test(trimmed)) {
    return `${trimmed.slice(0, 2)}:${trimmed.slice(2, 4)}`;
  }
  if (/^\d{4}$/.test(trimmed)) {
    return `${trimmed.slice(0, 2)}:${trimmed.slice(2, 4)}`;
  }
  if (trimmed.includes(':')) {
    const [h, m] = trimmed.split(':');
    return h && m ? `${h.padStart(2, '0')}:${m.padStart(2, '0')}` : trimmed;
  }
  return trimmed;
}

function formatDayMonth(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0');
  const month = MONTHS_SHORT[date.getMonth()] ?? '';
  return `${day} ${month}`;
}

function formatClockFromDate(value: string): string | null {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** Mock format: "09 Apr, 09:30" — falls back to assignment created_at when study date/time missing. */
export function formatPeerReviewStudyDateTime(row: PeerReview): string {
  const fields = getPeerReviewStudyFields(row);
  const clockFromTime = fields.study_time ? parseClock(fields.study_time) : null;

  if (fields.study_date?.trim()) {
    const date = new Date(fields.study_date.trim());
    if (!Number.isNaN(date.getTime())) {
      const dayMonth = formatDayMonth(date);
      const clock = clockFromTime ?? formatClockFromDate(fields.study_date.trim());
      return clock ? `${dayMonth}, ${clock}` : dayMonth;
    }
  }

  if (clockFromTime) {
    return clockFromTime;
  }

  const created = row.created_at?.trim();
  if (created) {
    const date = new Date(created);
    if (!Number.isNaN(date.getTime())) {
      const clock = formatClockFromDate(created);
      return clock ? `${formatDayMonth(date)}, ${clock}` : formatDayMonth(date);
    }
  }

  return '—';
}

/** @deprecated Prefer formatPeerReviewStudyDateTime */
export function formatPeerReviewTime(row: PeerReview): string {
  return formatPeerReviewStudyDateTime(row);
}

export function formatPeerReviewModalityLabel(modality?: string | null): string {
  const trimmed = modality?.trim();
  if (!trimmed) {
    return '—';
  }
  const code = trimmed.toUpperCase();
  if (code === 'XR' || code === 'DX' || code === 'CR' || code === 'X-RAY' || code === 'XRAY') {
    return 'X-ray';
  }
  if (code === 'US' || code === 'USG' || code === 'ULTRASOUND') {
    return 'Ultrasound';
  }
  if (code === 'MR' || code === 'MRI') {
    return 'MRI';
  }
  if (code === 'CT') {
    return 'CT';
  }
  return formatImagingModalityDisplay(code);
}

export function peerReviewModalityClass(modality?: string | null): string {
  const label = formatPeerReviewModalityLabel(modality).toLowerCase();
  if (label.includes('x-ray') || label === 'xr' || label === 'dx' || label === 'cr') {
    return 'peer-review-modality-pill--xray';
  }
  if (label.includes('ultrasound') || label === 'us' || label === 'usg') {
    return 'peer-review-modality-pill--ultrasound';
  }
  if (label.includes('mri') || label === 'mr') {
    return 'peer-review-modality-pill--mri';
  }
  if (label === 'ct') {
    return 'peer-review-modality-pill--ct';
  }
  return 'peer-review-modality-pill--default';
}

export function formatPeerReviewPriorityLabel(priority?: string | null): string {
  return formatPriorityDisplay(priority ?? undefined) || '—';
}

export function formatPeerReviewAge(age?: string | null): string {
  const trimmed = age?.trim();
  if (!trimmed || trimmed.toUpperCase() === 'N/A') {
    return '—';
  }
  if (/^\d+$/.test(trimmed)) {
    return `${trimmed}y`;
  }
  // Normalize "12 years" / "12 year" / "12 y" without optional-quantifier regex.
  const lower = trimmed.toLowerCase();
  for (const suffix of [' years', ' year']) {
    if (lower.endsWith(suffix)) {
      const num = trimmed.slice(0, trimmed.length - suffix.length).trim();
      if (/^\d+$/.test(num)) {
        return `${num}y`;
      }
    }
  }
  // "12y" / "12 y"
  let end = trimmed.length;
  while (end > 0 && ' \t\n\r\f\v'.includes(trimmed.charAt(end - 1))) {
    end--;
  }
  if (end > 0 && trimmed.charAt(end - 1).toLowerCase() === 'y') {
    const num = trimmed.slice(0, end - 1).trim();
    if (/^\d+$/.test(num)) {
      return `${num}y`;
    }
  }
  return trimmed;
}

export function isPeerReviewUrgent(priority?: string | null, isCritical?: boolean | null): boolean {
  if (isCritical === true) {
    return true;
  }
  return priorityUsesUrgentPill(priority ?? undefined);
}

function peerReviewStudyKey(row: PeerReview): string {
  const uid = row.study_instance_uid?.trim() || row.Study?.study_instance_uid?.trim() || '';
  if (uid) {
    return `uid:${uid}`;
  }
  if (row.study_id_fk != null) {
    return `study:${row.study_id_fk}`;
  }
  return `peer:${row.id}`;
}

/** Prefer active (Assigned) over Submitted, then higher level, then newer id. */
function peerReviewDedupeRank(row: PeerReview): number {
  const assignedBoost = String(row.status).toLowerCase() === 'assigned' ? 1_000_000 : 0;
  const level = Number(row.level) || 0;
  return assignedBoost + level * 1_000 + Number(row.id || 0);
}

/**
 * Admin / technician overview: one row per study when multiple radiologists
 * were assigned peer review on the same study.
 */
export function dedupePeerReviewsByStudy(rows: PeerReview[]): PeerReview[] {
  if (rows.length <= 1) {
    return rows;
  }

  const bestByStudy = new Map<string, PeerReview>();
  for (const row of rows) {
    const key = peerReviewStudyKey(row);
    const prev = bestByStudy.get(key);
    if (!prev || peerReviewDedupeRank(row) > peerReviewDedupeRank(prev)) {
      bestByStudy.set(key, row);
    }
  }

  const seen = new Set<string>();
  const deduped: PeerReview[] = [];
  for (const row of rows) {
    const key = peerReviewStudyKey(row);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    const chosen = bestByStudy.get(key);
    if (chosen) {
      deduped.push(chosen);
    }
  }
  return deduped;
}

export function formatPeerReviewReportStatus(
  status: PeerReviewStatus | string | null | undefined,
  reportStatus?: string | null
): string {
  // Peer Review page: drive from assignment status first (Assigned / Submitted).
  const peer = status ? String(status).trim().toLowerCase() : '';
  if (peer === 'assigned') {
    return 'In review';
  }
  if (peer === 'submitted') {
    return 'Completed';
  }
  if (peer === 'expired') {
    return 'Manual action required';
  }

  const fromStudy = reportStatus?.trim();
  if (fromStudy && fromStudy.toUpperCase() !== 'N/A') {
    const studyKey = fromStudy.toLowerCase();
    if (studyKey === 'submitted') {
      return 'Completed';
    }
    if (studyKey === 'assigned') {
      return 'In review';
    }
    return fromStudy;
  }

  if (!status) {
    return '—';
  }
  return String(status);
}

export function peerReviewReportStatusClass(label: string): string {
  const key = label.trim().toLowerCase();
  if (key.includes('review') || key === 'assigned') {
    return 'peer-review-status-pill--in-review';
  }
  if (key.includes('submit') || key.includes('complete')) {
    return 'peer-review-status-pill--submitted';
  }
  return 'peer-review-status-pill--default';
}

export function displayOrDash(value?: string | null): string {
  const trimmed = value?.trim();
  if (!trimmed || trimmed.toUpperCase() === 'N/A') {
    return '—';
  }
  return trimmed;
}
